import React, { useState, useEffect } from 'react';
import {
  FaPlus,
  FaTrash,
  FaLink,
  FaUser,
  FaQuoteRight,
  FaInfoCircle,
  FaTags,
  FaCamera,
  FaImage,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '@/utils/api';
import ImageUpload from '../ImageUpload/ImageUpload';
import {
  SettingsPanel,
  SettingsRow,
  SettingsSaveButton,
  SettingsNote,
  SettingsSaveStatus,
} from './primitives';
import './ProfileSettings.scss';

interface ProfileLink {
  label: string;
  url: string;
}

interface ProfileData {
  displayName: string;
  bio: string;
  headline: string;
  avatarUrl: string;
  bannerUrl: string;
  links: ProfileLink[];
  interests: string[];
}

export const ProfileSettings: React.FC = () => {
  const [form, setForm] = useState<ProfileData>({
    displayName: '',
    bio: '',
    headline: '',
    avatarUrl: '',
    bannerUrl: '',
    links: [],
    interests: [],
  });
  const [interestInput, setInterestInput] = useState('');
  const [saveStatus, setSaveStatus] = useState<SettingsSaveStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const [originalDisplayName, setOriginalDisplayName] = useState('');
  const [dnStatus, setDnStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get('/user-profile');
        setForm({
          displayName: data.displayName || '',
          bio: data.bio || '',
          headline: data.headline || '',
          avatarUrl: data.avatarUrl || '',
          bannerUrl: data.bannerUrl || '',
          links: Array.isArray(data.links) ? data.links : [],
          interests: Array.isArray(data.interests) ? data.interests : [],
        });
        setOriginalDisplayName(data.displayName || '');
      } catch {
        // non-fatal — form stays empty
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const trimmed = form.displayName.trim();
    if (trimmed.length === 0 || trimmed.toLowerCase() === originalDisplayName.trim().toLowerCase()) {
      setDnStatus('idle');
      return;
    }
    setDnStatus('checking');
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get<{ available: boolean }>(
          '/user-profile/display-name/availability',
          { params: { value: trimmed } }
        );
        setDnStatus(data.available ? 'available' : 'taken');
      } catch {
        setDnStatus('idle');
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [form.displayName, originalDisplayName]);

  const handleField = (field: keyof Pick<ProfileData, 'displayName' | 'bio' | 'headline'>, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleAvatarUpload = async (url: string) => {
    setForm((f) => ({ ...f, avatarUrl: url }));
    try {
      await api.patch('/user/account/avatar', { avatarUrl: url });
      toast.success('Avatar updated');
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to update avatar';
      toast.error(msg);
    }
  };

  const handleBannerUpload = async (url: string) => {
    setForm((f) => ({ ...f, bannerUrl: url }));
    try {
      await api.patch('/user/account/banner', { bannerUrl: url });
      toast.success('Banner updated');
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to update banner';
      toast.error(msg);
    }
  };

  const addLink = () => {
    if (form.links.length >= 10) return;
    setForm((f) => ({ ...f, links: [...f.links, { label: '', url: '' }] }));
  };

  const updateLink = (index: number, field: 'label' | 'url', value: string) => {
    setForm((f) => {
      const links = [...f.links];
      links[index] = { ...links[index]!, [field]: value };
      return { ...f, links };
    });
  };

  const removeLink = (index: number) => {
    setForm((f) => ({ ...f, links: f.links.filter((_, i) => i !== index) }));
  };

  const addInterest = () => {
    const tag = interestInput.trim().replace(/^#/, '');
    if (!tag || form.interests.includes(tag) || form.interests.length >= 20) return;
    setForm((f) => ({ ...f, interests: [...f.interests, tag] }));
    setInterestInput('');
  };

  const removeInterest = (tag: string) => {
    setForm((f) => ({ ...f, interests: f.interests.filter((i) => i !== tag) }));
  };

  const handleInterestKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addInterest();
    }
  };

  const handleSave = async () => {
    setSaveStatus('saving');
    setErrorMessage('');
    try {
      await api.put('/user-profile', {
        displayName: form.displayName.trim() || undefined,
        bio: form.bio.trim() || undefined,
        headline: form.headline.trim() || undefined,
        links: form.links.filter((l) => l.url.trim()),
        interests: form.interests,
      });
      setOriginalDisplayName(form.displayName.trim());
      setDnStatus('idle');
      setSaveStatus('saved');
      toast.success('Profile saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to save profile';
      setSaveStatus('error');
      setErrorMessage(msg);
      toast.error(msg);
    }
  };

  if (isLoading) {
    return (
      <SettingsPanel title="Profile" description="Loading…">
        <div className="profile-settings-spinner" />
      </SettingsPanel>
    );
  }

  return (
    <SettingsPanel
      title="Profile"
      description="Update your public profile information"
    >
      <SettingsRow
        icon={<FaCamera />}
        title="Profile Picture"
        description="Upload your avatar (max 10MB)"
      >
        <ImageUpload
          onUploadComplete={handleAvatarUpload}
          currentImage={form.avatarUrl}
          endpoint="avatar"
          fieldName="avatar"
          maxSizeMB={10}
          className="profile-settings-avatar"
        />
      </SettingsRow>

      <SettingsRow
        icon={<FaImage />}
        title="Banner Image"
        description="Upload your profile banner (max 10MB)"
      >
        <ImageUpload
          onUploadComplete={handleBannerUpload}
          currentImage={form.bannerUrl}
          endpoint="banner"
          fieldName="banner"
          maxSizeMB={10}
          className="profile-settings-banner"
        />
      </SettingsRow>

      <SettingsRow
        icon={<FaUser />}
        title="Display Name"
        description="Your public display name (must be unique)"
      >
        <div className="profile-settings-field">
          <input
            id="ps-displayName"
            type="text"
            value={form.displayName}
            maxLength={30}
            placeholder="Your display name"
            onChange={(e) => handleField('displayName', e.target.value)}
            aria-describedby="ps-displayName-status"
          />
          {dnStatus !== 'idle' && (
            <span
              id="ps-displayName-status"
              className={`profile-settings-hint profile-settings-hint--${dnStatus}`}
            >
              {dnStatus === 'checking' && 'Checking availability…'}
              {dnStatus === 'available' && '✓ Available'}
              {dnStatus === 'taken' && '✗ Already taken'}
            </span>
          )}
        </div>
      </SettingsRow>

      <SettingsRow
        icon={<FaQuoteRight />}
        title="Headline"
        description="A short one-liner under your name"
      >
        <div className="profile-settings-field">
          <input
            id="ps-headline"
            type="text"
            value={form.headline}
            maxLength={100}
            placeholder="e.g. Artist · Creator · Collector"
            onChange={(e) => handleField('headline', e.target.value)}
          />
        </div>
      </SettingsRow>

      <SettingsRow
        icon={<FaInfoCircle />}
        title="Bio"
        description="Tell people about yourself"
      >
        <div className="profile-settings-field">
          <textarea
            id="ps-bio"
            value={form.bio}
            maxLength={500}
            rows={4}
            placeholder="Tell people about yourself…"
            onChange={(e) => handleField('bio', e.target.value)}
          />
          <span className="profile-settings-hint">{500 - form.bio.length} characters remaining</span>
        </div>
      </SettingsRow>

      <SettingsRow
        icon={<FaLink />}
        title="Links"
        description="Add your portfolio, socials, or any URL with a custom label"
        control={<span className="profile-settings-meta">{form.links.length}/10</span>}
      >
        <div className="profile-settings-link-list">
          {form.links.map((link, i) => (
            <div key={i} className="profile-settings-link-row">
              <FaLink className="profile-settings-link-icon" />
              <input
                type="text"
                className="profile-settings-link-label"
                placeholder="Label (e.g. Portfolio)"
                value={link.label}
                maxLength={50}
                onChange={(e) => updateLink(i, 'label', e.target.value)}
              />
              <input
                type="url"
                className="profile-settings-link-url"
                placeholder="https://…"
                value={link.url}
                maxLength={500}
                onChange={(e) => updateLink(i, 'url', e.target.value)}
              />
              <button
                type="button"
                className="profile-settings-link-remove"
                onClick={() => removeLink(i)}
                aria-label="Remove link"
              >
                <FaTrash />
              </button>
            </div>
          ))}
        </div>

        {form.links.length < 10 && (
          <button type="button" className="profile-settings-add-btn" onClick={addLink}>
            <FaPlus /> Add link
          </button>
        )}
      </SettingsRow>

      <SettingsRow
        icon={<FaTags />}
        title="Interests"
        description="Tags that describe what you're into. Press Enter or comma to add."
        control={<span className="profile-settings-meta">{form.interests.length}/20</span>}
      >
        <div className="profile-settings-tags">
          {form.interests.map((tag) => (
            <span key={tag} className="profile-settings-tag">
              #{tag}
              <button
                type="button"
                className="profile-settings-tag-remove"
                onClick={() => removeInterest(tag)}
                aria-label={`Remove ${tag}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>

        {form.interests.length < 20 && (
          <div className="profile-settings-interest-input">
            <input
              type="text"
              placeholder="Add an interest…"
              value={interestInput}
              onChange={(e) => setInterestInput(e.target.value)}
              onKeyDown={handleInterestKeyDown}
              maxLength={50}
            />
            <button type="button" className="profile-settings-add-btn" onClick={addInterest}>
              Add
            </button>
          </div>
        )}
      </SettingsRow>

      {errorMessage && (
        <SettingsNote tone="warning">
          <p>{errorMessage}</p>
        </SettingsNote>
      )}

      <SettingsSaveButton
        status={saveStatus}
        onClick={handleSave}
        disabled={saveStatus === 'saving' || dnStatus === 'taken' || dnStatus === 'checking'}
        idleLabel="Save changes"
      />
    </SettingsPanel>
  );
};

export default ProfileSettings;
